import { FlexColumn, MaterialIcon } from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { Button, ButtonOverrides } from 'baseui/button';
import { ButtonGroup } from 'baseui/button-group';
import { StyledDivider } from 'baseui/divider';
import { StatefulPopover } from 'baseui/popover';
import { LabelSmall } from 'baseui/typography';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ROAD_MAP_TYPE, SATELLITE_MAP_TYPE } from '../../constants/map';
import { SAFE_SPOTS, UNSAFE_SPOTS } from '../../constants/spots';
import { ZONE_FENCES } from '../../constants/zoneFences';
import { useStyles } from '../../hooks/useStyles';
import { isPublicPageState } from '../../storage/app';
import {
  mapTypeState,
  selectedSpotLayerState,
  selectedZoneFenceState,
  showDeliveryFencesLayerState,
  showFencesLayerState,
  showTrafficLayerState,
} from '../../storage/map';
import { StyledTooltip } from '../ui/StyledTooltip';

export function MapOptions() {
  const { theme, css } = useStyles();
  const [mapType, setMapType] = useRecoilState(mapTypeState);
  const [showTrafficLayer, setShowTrafficLayer] = useRecoilState(showTrafficLayerState);
  const [showFencesLayer, setShowFencesLayer] = useRecoilState(showFencesLayerState);
  const [showDeliveryFencesLayer, setShowDeliveryFencesLayer] = useRecoilState(showDeliveryFencesLayerState);
  const [selectedSpotLayer, setSelectedSpotLayer] = useRecoilState(selectedSpotLayerState);
  const [selectedZoneFenceLayer, setSelectedZoneFenceLayer] = useRecoilState(selectedZoneFenceState);
  const isPublicPage = useRecoilValue(isPublicPageState);

  const getSelectedMapType = () => {
    if (mapType === ROAD_MAP_TYPE) {
      return 0;
    }
    if (mapType === SATELLITE_MAP_TYPE) {
      return 1;
    }
  };

  const getSelectedLayers = () => {
    const selectedLayers = [];
    if (showTrafficLayer) selectedLayers.push(0);
    if (showFencesLayer) selectedLayers.push(1);
    if (showDeliveryFencesLayer) selectedLayers.push(2);
    return selectedLayers;
  };

  const onLayersChange = (_event: any, index: number) => {
    if (index === 0) {
      setShowTrafficLayer((prev) => !prev);
    } else if (index === 1) {
      setShowFencesLayer((prev) => !prev);
    } else if (index === 2) {
      setShowDeliveryFencesLayer((prev) => !prev);
    }
  };

  const onMapTypeChange = (_event: any, index: number) => {
    if (index === 0) {
      setMapType(ROAD_MAP_TYPE);
    } else if (index === 1) {
      setMapType(SATELLITE_MAP_TYPE);
    }
  };

  const onSafeSpotsChange = (_event: any, index: number) => {
    if (index === 0) {
      setSelectedSpotLayer('1');
    } else if (index === 1) {
      setSelectedSpotLayer('2');
    } else if (index === 2) {
      setSelectedSpotLayer('3');
    }
    if (selectedSpotLayer === `${index + 1}`) {
      setSelectedSpotLayer(undefined);
    }
  };

  const selectedSafeSpots = (() => {
    const selectedSpots = [];
    if (selectedSpotLayer === '1') selectedSpots.push(0);
    else if (selectedSpotLayer === '2') selectedSpots.push(1);
    else if (selectedSpotLayer === '3') selectedSpots.push(2);
    return selectedSpots;
  })();

  const onUnsafeSpotsChange = (_event: any, index: number) => {
    if (index === 0) {
      setSelectedSpotLayer('4');
    }
    if (selectedSpotLayer === `${index + 4}`) {
      setSelectedSpotLayer(undefined);
    }
  };

  const selectedUnsafeSpots = (() => {
    const selectedSpots = [];
    if (selectedSpotLayer === '4') selectedSpots.push(0);
    return selectedSpots;
  })();

  const onZoneFenceChange = (_event: any, index: number) => {
    if (index === 0) {
      setSelectedZoneFenceLayer('1');
    }
    if (selectedZoneFenceLayer === `${index + 1}`) {
      setSelectedZoneFenceLayer(undefined);
    }
  };

  const selectedZoneFences = (() => {
    const selectedZones = [];
    if (selectedZoneFenceLayer === '1') selectedZones.push(0);
    return selectedZones;
  })();

  const buttonOverrides: ButtonOverrides = {
    BaseButton: {
      style: {
        paddingTop: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        marginRight: '0.5px',
        marginLeft: '0.5px',
      },
    },
  };

  const iconClass = css({
    display: 'block',
    paddingTop: theme.sizing.scale400,
    paddingBottom: theme.sizing.scale400,
    paddingRight: theme.sizing.scale500,
    paddingLeft: theme.sizing.scale500,
  });

  return (
    <StatefulPopover
      content={({ close }) => (
        <Block padding={theme.sizing.scale600}>
          <LabelSmall marginBottom={theme.sizing.scale300}>Tipo de mapa</LabelSmall>
          <ButtonGroup
            mode={'radio'}
            selected={getSelectedMapType()}
            size="compact"
            onClick={onMapTypeChange}
          >
            <Button>Road map</Button>
            <Button>Satellite</Button>
          </ButtonGroup>
          <StyledDivider
            $size="cell"
            $style={{ marginTop: theme.sizing.scale600, marginBottom: theme.sizing.scale600 }}
          />
          <LabelSmall marginBottom={theme.sizing.scale300}>Layers</LabelSmall>
          <ButtonGroup
            mode={'radio'}
            selected={getSelectedLayers()}
            size="compact"
            onClick={onLayersChange}
          >
            <Button>Tráfico</Button>
            {!isPublicPage && <Button>Cercos</Button>}
            {!isPublicPage && <Button>P. Entrega</Button>}
          </ButtonGroup>
          <StyledDivider
            $size="cell"
            $style={{ marginTop: theme.sizing.scale600, marginBottom: theme.sizing.scale600 }}
          />
          <LabelSmall marginBottom={theme.sizing.scale300}>Paraderos</LabelSmall>
          <FlexColumn gap={theme.sizing.scale300}>
            <FlexColumn gap={0}>
              <LabelSmall
                marginBottom={theme.sizing.scale300}
                color={theme.colors.contentSecondary}
              >
                Seguros
              </LabelSmall>
              <ButtonGroup
                mode={'radio'}
                selected={selectedSafeSpots}
                size="compact"
                onClick={onSafeSpotsChange}
              >
                {SAFE_SPOTS.map((safeSpot, index) => (
                  <Button
                    key={safeSpot.title}
                    overrides={buttonOverrides}
                  >
                    <StyledTooltip
                      content={safeSpot.title}
                      showArrow={false}
                      ignoreBoundary
                      placement="top"
                    >
                      <span className={iconClass}>
                        <MaterialIcon
                          name={safeSpot.icon}
                          color={selectedSafeSpots.includes(index) ? theme.colors.contentInversePrimary : undefined}
                        />
                      </span>
                    </StyledTooltip>
                  </Button>
                ))}
              </ButtonGroup>
            </FlexColumn>
            <FlexColumn gap={0}>
              <LabelSmall
                marginBottom={theme.sizing.scale300}
                color={theme.colors.contentSecondary}
              >
                No seguros
              </LabelSmall>
              <ButtonGroup
                mode={'radio'}
                selected={selectedUnsafeSpots}
                size="compact"
                onClick={onUnsafeSpotsChange}
              >
                {UNSAFE_SPOTS.map((unsafeSpot, index) => (
                  <Button
                    key={unsafeSpot.title}
                    overrides={buttonOverrides}
                  >
                    <StyledTooltip
                      content={unsafeSpot.title}
                      showArrow={false}
                      ignoreBoundary
                      placement="top"
                    >
                      <span className={iconClass}>
                        <MaterialIcon
                          name={unsafeSpot.icon}
                          color={selectedUnsafeSpots.includes(index) ? theme.colors.contentInversePrimary : undefined}
                        />
                      </span>
                    </StyledTooltip>
                  </Button>
                ))}
              </ButtonGroup>
            </FlexColumn>
          </FlexColumn>
          <StyledDivider
            $size="cell"
            $style={{ marginTop: theme.sizing.scale600, marginBottom: theme.sizing.scale600 }}
          />
          <LabelSmall marginBottom={theme.sizing.scale300}>Zonas</LabelSmall>
          <ButtonGroup
            mode={'radio'}
            selected={selectedZoneFences}
            size="compact"
            onClick={onZoneFenceChange}
          >
            {ZONE_FENCES.map((zoneFence, index) => (
              <Button
                overrides={buttonOverrides}
                key={index}
              >
                <StyledTooltip
                  content={zoneFence.title}
                  showArrow={false}
                  ignoreBoundary
                  placement="top"
                >
                  <span className={iconClass}>
                    <MaterialIcon
                      name={zoneFence.icon}
                      color={selectedZoneFences.includes(index) ? theme.colors.contentInversePrimary : undefined}
                    />
                  </span>
                </StyledTooltip>
              </Button>
            ))}
          </ButtonGroup>
        </Block>
      )}
      placement={'topRight'}
      triggerType={'click'}
      overrides={{
        Inner: {
          style: {
            backgroundColor: theme.colors.backgroundPrimary,
          },
        },
      }}
    >
      <span
        className={css({
          position: 'fixed',
          bottom: theme.sizing.scale850,
          right: theme.sizing.scale1200,
          background: theme.colors.backgroundPrimary,
          boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
          borderRadius: theme.borders.buttonBorderRadius,
        })}
      >
        <Button
          shape="square"
          kind="tertiary"
          size="compact"
        >
          <MaterialIcon
            name="layers"
            size="18px"
          />
        </Button>
      </span>
    </StatefulPopover>
  );
}
